<script setup lang="ts">
/**
 * A game-styled banner across the top of the garage: what just happened.
 *
 * Driven entirely by `state/notification-banner` — it has no props and no way
 * to ask which caller sent the notice, exactly as `ui/RewardPopup.vue` cannot
 * ask which payout it is showing. The daily-quest claim is its first caller;
 * a failed deploy (`App.vue`) is its second, and the only thing that caller
 * gets to say beyond the words themselves is `kind: 'error'` — the accent
 * colour and the politeness of the announcement, nothing else. There is no
 * per-caller styling here, only per-`kind`.
 *
 * ── Position ──────────────────────────────────────────────────────────────
 *
 * Fixed just under the title bar, reading `--bar-h` — the bar's own measured
 * height, published by `GarageScreen` from a `ResizeObserver` on the real
 * element. That is the same variable the daily-quest card's own top padding
 * reads (`GarageLobby.vue`), so this sits exactly where that card sits,
 * without restating a pixel figure that the next tab-strip change would
 * silently invalidate.
 *
 * ── Non-blocking, deliberately ───────────────────────────────────────────────
 *
 * This is a notice, not a question: there is no scrim, focus is never moved to
 * it, and Escape does nothing here (compare `ui/ConfirmPurchase.vue` and
 * `ui/RewardPopup.vue`, both of which hold the screen because there is a
 * decision or a receipt worth pausing for — there is neither here). The
 * wrapper spans the width of the tab so the panel can centre itself, but is
 * `pointer-events: none`; only the panel — a few hundred pixels, not the
 * screen — can be clicked, so a claim made mid-shop never steals a click from
 * whatever is underneath.
 */
import { bannerNotice, dismissNotice, runNoticeAction } from '@/state/notification-banner';

/** Bar widths are the notice's own fractions; this only turns one into a length. */
const pct = (fraction: number): string => `${(fraction * 100).toFixed(1)}%`;
</script>

<template>
  <div class="wrap">
    <Transition name="drop">
      <div
        v-if="bannerNotice"
        class="panel"
        :class="{ error: bannerNotice.kind === 'error' }"
        role="status"
        :aria-live="bannerNotice.kind === 'error' ? 'assertive' : 'polite'"
        data-test="notification-banner"
        :data-kind="bannerNotice.kind ?? 'success'"
      >
        <div class="body">
          <p class="title" data-test="notification-banner-title">{{ bannerNotice.title }}</p>
          <p
            v-for="(line, i) in bannerNotice.lines"
            :key="i"
            class="line"
            data-test="notification-banner-line"
          >
            {{ line }}
          </p>

          <ul
            v-if="bannerNotice.stats?.length"
            class="stats"
            data-test="notification-banner-stats"
          >
            <li
              v-for="stat in bannerNotice.stats"
              :key="stat.label"
              class="stat"
              :class="{ worse: !stat.better }"
              :data-stat="stat.label"
            >
              <span class="stat-label">{{ stat.label }}</span>
              <span class="bar" aria-hidden="true">
                <i class="bar-kept" :style="{ width: pct(Math.min(stat.fromFill, stat.toFill)) }"></i>
                <i
                  class="bar-delta"
                  :style="{
                    left: pct(Math.min(stat.fromFill, stat.toFill)),
                    width: pct(Math.abs(stat.toFill - stat.fromFill)),
                  }"
                ></i>
              </span>
              <span class="stat-figures">
                <span class="was">{{ stat.from }}</span>
                <span class="arrow" aria-hidden="true">&rarr;</span>
                <span class="now">{{ stat.to }}</span>
              </span>
            </li>
          </ul>
        </div>
        <button
          v-if="bannerNotice.action"
          type="button"
          class="action"
          data-test="notification-banner-action"
          @click="runNoticeAction()"
        >
          {{ bannerNotice.action.label }}
        </button>
        <button
          type="button"
          class="close"
          data-test="notification-banner-close"
          aria-label="Dismiss notification"
          @click="dismissNotice()"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
          </svg>
        </button>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
/*
 * The wrapper spans the tab so the panel can centre itself under the bar
 * whatever width the window is, but never intercepts a click of its own — only
 * the panel does, below.
 */
.wrap {
  position: fixed;
  top: calc(var(--bar-h, 3.6rem) + 0.6rem);
  left: 0;
  right: 0;
  z-index: 96;
  display: flex;
  justify-content: center;
  padding: 0 0.75rem;
  pointer-events: none;
}

/* Below `RotatePrompt` (101) and `ConfirmPurchase` (100) — a claim confirmed
   underneath a purchase dialog waits, it does not queue-jump the spend it sits
   behind. Above `SettingsPanel` (60), `FullscreenPrompt` (90) and every ordinary
   garage window (40), because it is meant to be seen the moment it fires
   whatever else is open on the floor. */
/*
 * A stamped steel plate rather than a floating glass card — the `.embossed`
 * bevel recipe (title bar buttons, `GarageScreen.vue`) restated on a panel
 * instead of a button: an inset top highlight and bottom shade read as a
 * plate standing proud of the screen behind it, not a pane of frosted glass.
 * `--notice-accent` is the one thing `.error` below overrides; everything
 * else, including the hazard-tape strip along the top edge (the same faint
 * diagonal `.ribbon` uses on the battle summary's payout band), reads it
 * rather than repeating the rule.
 */
.panel {
  --notice-accent: var(--accent);
  --notice-accent-dim: var(--accent-dim);
  position: relative;
  overflow: hidden;
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  width: min(28rem, 100%);
  padding: 0.75rem 0.6rem 0.65rem 0.8rem;
  border: 1px solid color-mix(in srgb, var(--notice-accent-dim) 75%, black 25%);
  border-radius: 3px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 40%, rgba(0, 0, 0, 0.32) 100%),
    var(--steel-800, #12161c);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    inset 0 -1px 0 rgba(0, 0, 0, 0.55),
    inset 0 0 0 1px color-mix(in srgb, var(--notice-accent) 55%, transparent),
    0 2px 0 rgba(0, 0, 0, 0.5),
    0 0.8rem 1.8rem rgba(0, 0, 0, 0.55);
}

/* The hazard tape — a thin diagonal strip along the top edge, the game's own
   caution-diagonal (`.ribbon` on the result screen) cut down to a label
   rather than a full band, so a notice reads as tagged rather than glowing. */
.panel::before {
  content: '';
  position: absolute;
  inset: 0 0 auto 0;
  height: 4px;
  background: repeating-linear-gradient(
    -45deg,
    var(--notice-accent) 0 6px,
    rgba(0, 0, 0, 0.7) 6px 12px
  );
  opacity: 0.9;
  pointer-events: none;
}

/* A refusal, never a reward — the hostile red this game already uses for its
   one other destructive/danger control (`.embossed.danger` on Sign out, and
   `.bay-remove`), not a colour invented for this one banner. */
.panel.error {
  --notice-accent: #d2544a;
  --notice-accent-dim: #8a382f;
}

.body {
  flex: 1 1 auto;
  min-width: 0;
}

.title {
  margin: 0 0 0.2rem;
  color: var(--notice-accent);
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  text-shadow: 0 1px 0 rgba(0, 0, 0, 0.6);
}

.line {
  margin: 0.05rem 0 0;
  color: var(--steel-200, #d7dde3);
  font-size: 0.85rem;
  line-height: 1.4;
  font-variant-numeric: tabular-nums;
}

/* ── the before/after rows ───────────────────────────────────────────────────
 *
 * The same three-part row the shop's own stat list draws — label, bar, figure —
 * at the size a strip under the title bar can carry, so a player who has just
 * upgraded a turret is reading the *same picture* they were reading on the card
 * a moment before rather than a second presentation of the same fact.
 *
 * The bar is two segments on one track and not two overlaid bars. `--kept` is
 * the part that was already there, drawn in the muted steel of a figure nobody
 * needs to look at; `--delta` is the part that moved, drawn in green or red and
 * sitting *beside* the kept segment rather than over it. A worse stat therefore
 * shows red at the end of a shorter bar, which is the honest picture: the red is
 * the piece that is no longer there. */
.stats {
  margin: 0.45rem 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.28rem;
}

.stat {
  display: grid;
  grid-template-columns: minmax(0, 6.5rem) minmax(2.5rem, 1fr) auto;
  align-items: center;
  gap: 0.45rem;
}

.stat-label {
  overflow: hidden;
  font-size: 0.66rem;
  letter-spacing: 0.04em;
  color: var(--steel-400);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bar {
  position: relative;
  height: 0.3rem;
  border-radius: 1px;
  background: rgb(0 0 0 / 45%);
  box-shadow: inset 0 1px 0 rgb(0 0 0 / 40%);
  overflow: hidden;
}

.bar i {
  position: absolute;
  top: 0;
  bottom: 0;
  display: block;
}

.bar-kept {
  left: 0;
  background: var(--steel-500, #6c7a89);
}

/* The gain, in the green this garage already uses for a researched node and a
   better stat on a card. */
.bar-delta {
  background: #4caf6d;
  box-shadow: 0 0 6px rgb(76 175 109 / 45%);
}

/* The loss, in the same red a price the player cannot pay is drawn in. */
.stat.worse .bar-delta {
  background: #d9705f;
  box-shadow: none;
}

.stat-figures {
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 0.68rem;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.was {
  color: var(--steel-400);
  text-decoration: line-through;
  text-decoration-color: rgb(140 152 165 / 60%);
}

.arrow {
  color: var(--steel-500, #6c7a89);
}

.now {
  color: #4caf6d;
  font-weight: 600;
}

.stat.worse .now {
  color: #d9705f;
}

/* 44x44 CSS px, this game's tap-target floor — the close button is the only
   control on the notice and must not be the reason it lingers on a phone.
   Colour is the only thing that answers a hover or a focus — no fill behind
   it, so the cross reads as a mark on the plate rather than a web-UI icon
   button sitting on its own little tile. */
/* The one call to action a notice may carry. 44 px tall: the tap floor. */
.action {
  flex: none;
  align-self: center;
  min-height: 2.75rem;
  padding: 0 1rem;
  border: 1px solid var(--notice-accent);
  border-radius: 3px;
  background: color-mix(in srgb, var(--notice-accent) 22%, transparent);
  color: var(--steel-100, #eef1f4);
  font: inherit;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
}

.action:hover,
.action:focus-visible {
  background: color-mix(in srgb, var(--notice-accent) 38%, transparent);
}

.close {
  flex: none;
  display: grid;
  place-items: center;
  width: 2.75rem;
  height: 2.75rem;
  margin: -0.75rem -0.6rem -0.65rem 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--steel-400);
  cursor: pointer;
  transition: color 150ms ease-out;
}

.close:hover,
.close:focus-visible {
  color: var(--steel-200, #d7dde3);
}

/* Keyboard-only affordance, kept separate from the hover rule above so a
   pointer hover never draws it. */
.close:focus-visible {
  outline: 1px solid var(--notice-accent);
  outline-offset: 1px;
}

.close svg {
  width: 0.85rem;
  height: 0.85rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.6;
  stroke-linecap: round;
}

.drop-enter-active,
.drop-leave-active {
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
}

.drop-enter-from,
.drop-leave-to {
  transform: translateY(-0.6rem);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .drop-enter-active,
  .drop-leave-active {
    transition: none;
  }
}
</style>
